import type { ComponentType } from "react"
import HouseholdChoresPost from "./2026-08-08-household-chores"

/**
 * Long-form blog posts. Keyed order matters: BLOG_POSTS is chronological
 * (newest first), which is the order the list page renders.
 *
 * When shipping a new post:
 *   1. Add its JSX file next to this one, named YYYY-MM-DD-kebab-title.tsx.
 *   2. Add its metadata + Component to BLOG_POSTS below (at the top).
 *   3. Add its slug to scripts/build-seo-html.mjs (BLOG_POSTS_TO_PRERENDER).
 *   4. Add its URL to public/sitemap.xml.
 *
 * The parity between (2) and (3) matters — the app-side renders per-post SEO
 * at runtime, but the prerender emits the same SEO into a static shell so
 * unfurl bots (which don't run JS) see the correct <head>.
 */
export interface BlogPost {
  slug: string
  /** ISO date (YYYY-MM-DD). Used for display, sort, and JSON-LD `datePublished`. */
  date: string
  title: string
  description: string
  /** Byline author shown on the article header. */
  author?: string
  /** Optional URL the author byline links to (e.g. their GitHub). */
  authorUrl?: string
  /** Optional hero/splash image served from /public (e.g. "/blog/slug.png"). Also used as the per-post og:image. */
  image?: string
  /** Alt text for {@link image}; set whenever image is set. */
  imageAlt?: string
  Component: ComponentType
}

export const BLOG_POSTS: readonly BlogPost[] = [
  {
    slug: "2026-08-08-household-chores",
    date: "2026-08-08",
    title: "How Retroboard became a household allowance system",
    description:
      "How I turned Retroboard, my own retro-and-polling side project, into my kids' weekly allowance board. They start the week with the money, and the game is keeping it.",
    author: "JJ",
    authorUrl: "https://github.com/jerold",
    image: "/blog/2026-08-08-household-chores.png",
    imageAlt:
      "A Retroboard board titled House Chores: a bonus pool plus a column for each kid showing their weekly allowance, and a column for Dad.",
    Component: HouseholdChoresPost,
  },
]

const BLOG_POSTS_BY_SLUG: ReadonlyMap<string, BlogPost> = new Map(
  BLOG_POSTS.map((p) => [p.slug, p]),
)

export function getBlogPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS_BY_SLUG.get(slug)
}

/**
 * Formats an ISO date as "August 6, 2026". UTC-anchored so the printed date
 * matches the slug regardless of the reader's timezone.
 */
export function formatBlogDate(iso: string): string {
  const d = new Date(iso + "T00:00:00Z")
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  })
}
